import { useEffect } from 'react';

const SITE_NAME = 'SkyLand Construction LLC';
const DEFAULT_SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://skylandconstruction.com').replace(/\/$/, '');
const DEFAULT_OG_IMAGE = '/assets/images/services2.jpg';
const DEFAULT_ROBOTS = 'index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1';
const NOINDEX_ROBOTS = 'noindex,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1';

function toAbsoluteUrl(value, fallbackPath = '/') {
  const raw = typeof value === 'string' && value.trim() ? value.trim() : fallbackPath;

  if (/^https?:\/\//i.test(raw)) {
    return raw;
  }

  const normalizedPath = raw.startsWith('/') ? raw : `/${raw}`;
  return `${DEFAULT_SITE_URL}${normalizedPath}`;
}

function upsertMeta(attribute, key, content) {
  if (typeof document === 'undefined' || !key) {
    return;
  }

  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content || '');
  element.setAttribute('data-seo-managed', 'true');
}

function upsertCanonical(href) {
  if (typeof document === 'undefined') {
    return;
  }

  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
  element.setAttribute('data-seo-managed', 'true');
}

function upsertAlternateLink(hreflang, href) {
  if (typeof document === 'undefined') {
    return;
  }

  let element = document.head.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'alternate');
    element.setAttribute('hreflang', hreflang);
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
  element.setAttribute('data-seo-managed', 'true');
}

function upsertStructuredData(structuredData) {
  if (typeof document === 'undefined') {
    return;
  }

  let element = document.head.querySelector('#seo-structured-data');

  if (!structuredData) {
    if (element) {
      element.remove();
    }
    return;
  }

  if (!element) {
    element = document.createElement('script');
    element.id = 'seo-structured-data';
    element.type = 'application/ld+json';
    element.setAttribute('data-seo-managed', 'true');
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(structuredData);
}

function buildWebPageStructuredData({ path, title, description, keywords }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description,
    url: toAbsoluteUrl(path),
    keywords: Array.isArray(keywords) ? keywords.join(', ') : '',
    inLanguage: 'en',
    isPartOf: {
      '@type': 'WebSite',
      name: SITE_NAME,
      url: DEFAULT_SITE_URL
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: DEFAULT_SITE_URL
    }
  };
}

export function setPageSeo(config = {}) {
  if (typeof document === 'undefined') {
    return;
  }

  const title = config.title || SITE_NAME;
  const description = config.description || 'SkyLand Construction LLC - Professional construction and engineering services in UAE.';
  const keywords = Array.isArray(config.keywords) ? config.keywords : [];
  const pagePath = config.path || (typeof window !== 'undefined' ? window.location.pathname : '/');
  const canonicalUrl = toAbsoluteUrl(config.canonicalPath || pagePath);
  const pageUrl = toAbsoluteUrl(config.ogPath || pagePath);
  const ogImage = toAbsoluteUrl(config.ogImage || DEFAULT_OG_IMAGE);
  const ogType = config.ogType || 'website';
  const robots = config.robots || DEFAULT_ROBOTS;
  const hasStructuredData = Object.prototype.hasOwnProperty.call(config, 'structuredData');
  const structuredData = hasStructuredData
    ? config.structuredData
    : buildWebPageStructuredData({
      path: pagePath,
      title,
      description,
      keywords
    });

  document.title = title;

  upsertMeta('name', 'description', description);
  upsertMeta('name', 'keywords', keywords.join(', '));
  upsertMeta('name', 'robots', robots);
  upsertMeta('name', 'googlebot', robots);
  upsertMeta('name', 'author', SITE_NAME);

  upsertMeta('property', 'og:site_name', SITE_NAME);
  upsertMeta('property', 'og:type', ogType);
  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:url', pageUrl);
  upsertMeta('property', 'og:image', ogImage);
  upsertMeta('property', 'og:image:secure_url', ogImage);
  upsertMeta('property', 'og:image:alt', config.ogImageAlt || `${SITE_NAME} visual`);
  upsertMeta('property', 'og:locale', 'en_AE');

  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', title);
  upsertMeta('name', 'twitter:description', description);
  upsertMeta('name', 'twitter:image', ogImage);
  upsertMeta('name', 'twitter:image:alt', config.twitterImageAlt || config.ogImageAlt || `${SITE_NAME} visual`);

  upsertCanonical(canonicalUrl);
  upsertAlternateLink('en', canonicalUrl);
  upsertAlternateLink('x-default', canonicalUrl);
  upsertStructuredData(structuredData);
}

export function usePageSeo(config) {
  useEffect(() => {
    setPageSeo(config);
  }, [config]);
}

export const HOME_PAGE_SEO = {
  title: 'SkyLand Construction UAE | EPC, Civil, Infrastructure & Fit-Out Experts',
  description:
    'SkyLand Construction delivers EPC, civil, structural, infrastructure, and fit-out services across the UAE with quality, safety, and on-time execution.',
  keywords: [
    'SkyLand Construction',
    'UAE construction company',
    'EPC services UAE',
    'civil and structural contractor',
    'infrastructure construction UAE',
    'fit-out contractor Ras Al Khaimah',
    'engineering procurement construction',
    'commercial and residential construction UAE'
  ],
  path: '/',
  canonicalPath: '/',
  ogImage: '/assets/images/services2.jpg'
};

export const ABOUT_PAGE_SEO = {
  title: 'Who We Are | About SkyLand Construction Since 2009',
  description:
    'Learn how SkyLand Construction has delivered trusted industrial, commercial, residential, and infrastructure projects in the UAE since 2009.',
  keywords: [
    'about SkyLand Construction',
    'who we are SkyLand',
    'Ras Al Khaimah construction company',
    'UAE contracting company',
    'industrial and commercial contractor UAE',
    'residential villas contractor UAE',
    'construction management UAE',
    'sustainable construction UAE'
  ],
  path: '/about_us',
  canonicalPath: '/about_us',
  ogImage: '/assets/images/services1.jpg'
};

export const WHAT_WE_DO_PAGE_SEO = {
  title: 'What We Do | EPC, Civil, MEP, Infrastructure & Project Delivery',
  description:
    'Explore SkyLand Construction delivery phases and multidisciplinary services including EPC, civil, MEP, infrastructure, and fit-out execution.',
  keywords: [
    'what we do SkyLand',
    'EPC contractor UAE',
    'MEP services UAE',
    'civil construction services UAE',
    'infrastructure contractor UAE',
    'project delivery framework',
    'commissioning and handover construction',
    'engineering services Ras Al Khaimah'
  ],
  path: '/what_we_do',
  canonicalPath: '/what_we_do',
  ogImage: '/assets/images/services1.jpg'
};

export const PROJECTS_PAGE_SEO = {
  title: 'Journey So Far | SkyLand Construction Projects in UAE',
  description:
    'View the SkyLand Construction project portfolio across industrial, residential villas, infrastructure, fit-outs, and engineering survey works.',
  keywords: [
    'SkyLand projects UAE',
    'construction portfolio UAE',
    'industrial projects UAE',
    'residential villa construction UAE',
    'infrastructure projects UAE',
    'fit-out projects UAE',
    'engineering survey projects',
    'journey so far SkyLand'
  ],
  path: '/projects',
  canonicalPath: '/projects',
  ogImage: '/assets/images/services2.jpg'
};

export const BLOG_PAGE_SEO = {
  title: 'Industry Insights | UAE Construction & Real Estate Updates',
  description:
    'Read industry insights, UAE construction news, and real estate updates curated for developers, project owners, and engineering teams.',
  keywords: [
    'industry insights UAE',
    'construction news UAE',
    'real estate updates UAE',
    'engineering trends UAE',
    'construction market insights',
    'SkyLand blog',
    'project planning insights',
    'construction innovation UAE'
  ],
  path: '/blog',
  canonicalPath: '/blog',
  ogImage: '/assets/images/services2.jpg',
  ogType: 'article',
  structuredData: {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Industry Insights',
    description:
      'Construction and real estate industry news, trends, and market updates relevant to the UAE and the wider region.',
    url: toAbsoluteUrl('/blog'),
    inLanguage: 'en',
    isPartOf: {
      '@type': 'WebSite',
      name: SITE_NAME,
      url: DEFAULT_SITE_URL
    },
    about: ['Construction', 'Engineering', 'Real Estate', 'Market Trends']
  }
};

export const BLOG_POST_PAGE_SEO = {
  title: 'Technology in Construction Operations | SkyLand Industry Insights',
  description:
    'Read practical insights on construction technology adoption, project controls, and execution workflows from the SkyLand Industry Insights desk.',
  keywords: [
    'construction technology UAE',
    'project controls insight',
    'construction operations blog',
    'SkyLand industry insights',
    'digital construction workflow'
  ],
  path: '/blog-post',
  canonicalPath: '/blog-post',
  ogImage: '/assets/images/services2.jpg',
  ogType: 'article',
  structuredData: {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: 'Technology in Construction Operations',
    description:
      'How construction teams can apply practical technology choices to improve planning, delivery quality, and schedule confidence.',
    datePublished: '2026-01-10',
    dateModified: '2026-01-10',
    author: {
      '@type': 'Organization',
      name: SITE_NAME
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: DEFAULT_SITE_URL
    },
    mainEntityOfPage: toAbsoluteUrl('/blog-post'),
    image: [toAbsoluteUrl('/assets/images/services2.jpg')]
  }
};

export const BLOG_LIST_PAGE_SEO = {
  title: 'Construction Delivery Frameworks | SkyLand Industry Insights',
  description:
    'Explore curated articles on planning, procurement, and construction delivery systems that improve cost, quality, and timeline outcomes.',
  keywords: [
    'construction delivery frameworks',
    'procurement and execution insights',
    'project delivery article',
    'construction planning blog'
  ],
  path: '/blog-list',
  canonicalPath: '/blog-list',
  ogImage: '/assets/images/services2.jpg',
  ogType: 'article',
  structuredData: {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: 'Construction Delivery Frameworks',
    description:
      'A practical overview of construction delivery frameworks and where each model fits based on project complexity and risk.',
    datePublished: '2026-01-17',
    dateModified: '2026-01-17',
    author: {
      '@type': 'Organization',
      name: SITE_NAME
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: DEFAULT_SITE_URL
    },
    mainEntityOfPage: toAbsoluteUrl('/blog-list'),
    image: [toAbsoluteUrl('/assets/images/services2.jpg')]
  }
};

export const BLOG_GRID_PAGE_SEO = {
  title: 'Site Execution Checklists | SkyLand Industry Insights',
  description:
    'Review execution checklists and quality-control guidance for safer, faster, and more predictable construction delivery.',
  keywords: [
    'site execution checklist',
    'construction quality control',
    'project handover planning',
    'construction management article'
  ],
  path: '/blog-grid',
  canonicalPath: '/blog-grid',
  ogImage: '/assets/images/services1.jpg',
  ogType: 'article',
  structuredData: {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: 'Site Execution Checklists',
    description:
      'Execution-stage checklists construction teams can use to reduce rework and improve on-site quality control.',
    datePublished: '2026-01-24',
    dateModified: '2026-01-24',
    author: {
      '@type': 'Organization',
      name: SITE_NAME
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: DEFAULT_SITE_URL
    },
    mainEntityOfPage: toAbsoluteUrl('/blog-grid'),
    image: [toAbsoluteUrl('/assets/images/services1.jpg')]
  }
};

export const FAQ_PAGE_SEO = {
  title: 'Common Concerns | Construction FAQs | SkyLand Construction',
  description:
    'Find clear answers on SkyLand services, project process, revisions, quality, safety, warranties, and support for construction projects in the UAE.',
  keywords: [
    'construction FAQ UAE',
    'common concerns construction',
    'SkyLand FAQ',
    'construction quality and safety',
    'project management questions',
    'construction warranty UAE',
    'MEP and EPC FAQ',
    'contact construction support UAE'
  ],
  path: '/faq',
  canonicalPath: '/faq',
  ogImage: '/assets/images/services1.jpg',
  structuredData: {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What types of construction services do you offer?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'We deliver services for residential, commercial, industrial, and infrastructure projects with integrated engineering and execution support.'
        }
      },
      {
        '@type': 'Question',
        name: 'How do you manage communication during a project?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Our project teams follow a structured communication workflow with regular updates, milestone tracking, and transparent reporting for stakeholders.'
        }
      },
      {
        '@type': 'Question',
        name: 'How do you ensure quality and safety on site?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'We implement strict quality control checks, safety protocols, and ongoing team training to maintain compliance and protect all personnel and assets.'
        }
      },
      {
        '@type': 'Question',
        name: 'Do you provide warranties or guarantees for completed work?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. We provide applicable workmanship and material warranties based on contract terms and resolve covered issues promptly.'
        }
      }
    ]
  }
};

export const CONTACT_PAGE_SEO = {
  title: 'Contact SkyLand Construction | Start Your Project in the UAE',
  description:
    'Contact SkyLand Construction LLC for EPC, civil, infrastructure, fit-out, and engineering services across the UAE.',
  keywords: [
    'contact SkyLand Construction',
    'construction company contact UAE',
    'EPC contractor contact',
    'Ras Al Khaimah construction office',
    'construction consultation UAE',
    'request construction quote UAE',
    'engineering contractor contact',
    'SkyLand Construction phone and email'
  ],
  path: '/contactus',
  canonicalPath: '/contactus',
  ogImage: '/assets/images/contact_us.jpg',
  structuredData: {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact SkyLand Construction',
    description:
      'Get in touch with SkyLand Construction for project inquiries, consultations, and partnership opportunities in the UAE.',
    url: toAbsoluteUrl('/contactus'),
    inLanguage: 'en',
    isPartOf: {
      '@type': 'WebSite',
      name: SITE_NAME,
      url: DEFAULT_SITE_URL
    },
    mainEntity: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: DEFAULT_SITE_URL,
      contactPoint: [
        {
          '@type': 'ContactPoint',
          contactType: 'customer support',
          telephone: '+97172435757',
          email: 'info@skylandconstruction.com',
          areaServed: 'AE',
          availableLanguage: ['en']
        }
      ]
    }
  }
};

export const TERMS_PAGE_SEO = {
  title: 'Terms & Conditions | SkyLand Construction LLC',
  description:
    'Read the official SkyLand Construction LLC website terms and conditions for acceptable use, liability, intellectual property, and legal compliance.',
  keywords: [
    'SkyLand terms and conditions',
    'construction website terms',
    'legal terms SkyLand Construction',
    'website usage terms UAE',
    'SkyLand legal policy'
  ],
  path: '/terms',
  canonicalPath: '/terms',
  ogImage: '/assets/images/services2.jpg',
  structuredData: {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Terms & Conditions',
    description:
      'Terms and conditions governing use of the SkyLand Construction website and digital content.',
    url: toAbsoluteUrl('/terms'),
    inLanguage: 'en',
    isPartOf: {
      '@type': 'WebSite',
      name: SITE_NAME,
      url: DEFAULT_SITE_URL
    }
  }
};

function normalizePath(pathname = '/') {
  const raw = typeof pathname === 'string' && pathname.trim() ? pathname.trim() : '/';
  const withoutQuery = raw.split('?')[0].split('#')[0];
  const withLeadingSlash = withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`;

  if (withLeadingSlash.length > 1 && withLeadingSlash.endsWith('/')) {
    return withLeadingSlash.slice(0, -1);
  }

  return withLeadingSlash || '/';
}

function createNoIndexSeo(path) {
  const normalizedPath = normalizePath(path);
  const displayName = normalizedPath
    .replace(/^\//, '')
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase()) || 'Page';

  return {
    title: `${displayName} | ${SITE_NAME}`,
    description: `${SITE_NAME} website page.`,
    keywords: ['SkyLand Construction', 'construction services UAE'],
    path: normalizedPath,
    canonicalPath: '/',
    ogImage: DEFAULT_OG_IMAGE,
    robots: NOINDEX_ROBOTS,
    structuredData: null
  };
}

const INDEXABLE_ROUTE_SEO = {
  '/': HOME_PAGE_SEO,
  '/about': ABOUT_PAGE_SEO,
  '/about_us': ABOUT_PAGE_SEO,
  '/what_we_do': WHAT_WE_DO_PAGE_SEO,
  '/services-epc': WHAT_WE_DO_PAGE_SEO,
  '/projects': PROJECTS_PAGE_SEO,
  '/projects-showcase': PROJECTS_PAGE_SEO,
  '/blog': BLOG_PAGE_SEO,
  '/blog-post': BLOG_POST_PAGE_SEO,
  '/blog-list': BLOG_LIST_PAGE_SEO,
  '/blog-grid': BLOG_GRID_PAGE_SEO,
  '/faq': FAQ_PAGE_SEO,
  '/contactus': CONTACT_PAGE_SEO,
  '/terms': TERMS_PAGE_SEO,
  '/terms&conditions': TERMS_PAGE_SEO
};

const NOINDEX_ROUTES = new Set([
  '/home-construction3',
  '/home-transport',
  '/home-agriculture',
  '/home-solar-energy',
  '/home-oilgas-plant',
  '/home-page8',
  '/home-industries9',
  '/home-factory10',
  '/home-construction11',
  '/home-conditioning-repair12',
  '/about-2',
  '/services-1',
  '/services-2',
  '/services-heavy-equipment',
  '/services-mep',
  '/services-infrastructure',
  '/services-civil',
  '/services-steel-structure',
  '/services-civil-defense',
  '/services-fitout',
  '/services-survey',
  '/services-test',
  '/services-epc-test',
  '/project-masonry',
  '/project-carousel',
  '/project-detail',
  '/shop',
  '/shop-detail',
  '/shopping-cart',
  '/checkout',
  '/wishlist',
  '/pricing-plan',
  '/icon-font',
  '/team',
  '/team-single',
  '/error403',
  '/error404',
  '/error405'
]);

export function getRouteSeoConfig(pathname = '/') {
  const normalizedPath = normalizePath(pathname);
  const lowerPath = normalizedPath.toLowerCase();

  if (lowerPath.startsWith('/admin')) {
    return {
      ...createNoIndexSeo(normalizedPath),
      title: `Admin | ${SITE_NAME}`
    };
  }

  if (INDEXABLE_ROUTE_SEO[lowerPath]) {
    return {
      ...INDEXABLE_ROUTE_SEO[lowerPath],
      path: normalizedPath
    };
  }

  if (NOINDEX_ROUTES.has(lowerPath)) {
    return createNoIndexSeo(normalizedPath);
  }

  return {
    ...createNoIndexSeo(normalizedPath),
    title: `Page Not Found | ${SITE_NAME}`,
    description: 'The requested page was not found on the SkyLand Construction website.'
  };
}
